
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { handleConnectionError } from '@/utils/connectionErrorHandler';

interface SEOData {
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
}

export const useSEO = (pageType: string, pageIdentifier?: string) => {
  return useQuery({
    queryKey: ['seo', pageType, pageIdentifier],
    queryFn: async (): Promise<SEOData> => {
      try {
        // First try to get specific page SEO if identifier is provided
        if (pageIdentifier) {
          const { data: specificSEO, error: specificError } = await supabase
            .from('seo_settings')
            .select('*')
            .eq('page_type', pageType)
            .eq('page_identifier', pageIdentifier)
            .maybeSingle();
          
          if (specificError) {
            handleConnectionError(specificError, 'useSEO - specific');
            throw specificError;
          }
          
          if (specificSEO) {
            return {
              meta_title: specificSEO.meta_title,
              meta_description: specificSEO.meta_description,
              meta_keywords: specificSEO.meta_keywords,
              og_title: specificSEO.og_title,
              og_description: specificSEO.og_description,
              og_image: specificSEO.og_image,
            };
          }
        }
        
        // Fallback to general page type SEO
        const { data: generalSEO, error: generalError } = await supabase
          .from('seo_settings')
          .select('*')
          .eq('page_type', pageType)
          .is('page_identifier', null)
          .maybeSingle();
        
        if (generalError) {
          handleConnectionError(generalError, 'useSEO - general');
          throw generalError;
        }
        
        if (generalSEO) {
          return {
            meta_title: generalSEO.meta_title,
            meta_description: generalSEO.meta_description,
            meta_keywords: generalSEO.meta_keywords,
            og_title: generalSEO.og_title,
            og_description: generalSEO.og_description,
            og_image: generalSEO.og_image,
          };
        }
        
        // Return empty object if no SEO settings found
        return {};
      } catch (error) {
        handleConnectionError(error as Error, 'useSEO');
        // Return empty object instead of throwing to prevent UI breaking
        return {};
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.code === '401' || error?.message?.includes('JWT')) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    // Add timeout to prevent infinite loading
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
